import {FormConfig} from '../../../models/form-config';
import {
    OfferModification,
    OfferModificationOption,
    PriceFrequency
} from '../../../../core/models/offer/offer-modification.model';
import {FieldConfig} from '../../../models/field-config';
import {FieldType} from '../../../models/field-type';
import {Validator} from '../../../models/validator.enum';
import {LanguageService} from '../../../services/language.service';
import {OfferModificationDestination} from '../../../../core/consts/offer-modification-destination.enum';
import {
    AcVariantsModificationOption,
    OfferModificationCode
} from '../../../../core/consts/offer-modification-code.enum';
import {OfferModificationsService} from '../../../../core/services/impl/offer-modifications/offer-modifications.service';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {AcVariantsSectionConfigBase} from './ac-variants-section-config-base';
import * as _ from 'lodash';

export class AcVariantsSectionConfig extends AcVariantsSectionConfigBase {

    constructor(private languageService: LanguageService,
                public offerModificationsService: OfferModificationsService,
                private priceFrequency?: PriceFrequency) {
        super(offerModificationsService);
    }

    createConfig(destination: OfferModificationDestination, modifications: OfferModification[], tilesMode: boolean, initialConfig = false): FormConfig {
        let config: FormConfig = {
            formKey: destination,
            fieldsConfig: []
        };

        config.fieldsConfig = [];
        modifications.forEach(modification => config.fieldsConfig.push(...this.createFieldConfig(modification, tilesMode, initialConfig)));

        const vehicleValueFieldConfig = this.createVehicleValueFieldConfig();
        if (this.offerModificationsService.vehicleValueModificationEnabled && vehicleValueFieldConfig) {
            config.fieldsConfig.push(vehicleValueFieldConfig);
        }

        return config;
    }

    private createFieldConfig(modification: OfferModification, tilesMode: boolean, initialConfig: boolean): FieldConfig[] {
        const modificationConfig = this.offerModificationsService.configService.getModificationConfig(<OfferModificationCode>modification.code);
        const configs: any[] = [{
            fieldKey: modification.code,
            type: FieldType.Radio,
            tooltipIconOutlined: true,
            radioInline: true,
            tilesMode: tilesMode,
            validators: [Validator.REQUIRED],
            caption: this.languageService.getDescription(<any[]>modification.labels),
            description: isNotNullOrUndefined(modificationConfig.tooltip) ? modificationConfig.tooltip : null,
            radioConfig: this.createRadioConfigs(modification, initialConfig)
        }];

        if (this.offerModificationsService.isModificationWithSelect(modification.code)) {
            configs.push({
                fieldKey: `${modification.code}-value`,
                type: FieldType.Select,
                caption: 'OFFER_MODIFICATIONS.insuranceSum',
                tooltipIconOutlined: true,
                options: {
                    item: 'key',
                    label: { label: 'label' },
                    items: this.createSelectOptions(modification)
                },
                select: {
                    dropdownPosition: 'bottom',
                    multiple: false,
                    searchable: true,
                }
            })
        }

        return configs;
    }

    private getDefaultPositiveOptionForModificationWithSelect(modification: OfferModification): OfferModificationOption {
        if (_.isString(modification.options[0].value)) { // option with minimum price
            return this.offerModificationsService.getOptionWithMinimumNonZeroPrice(modification);
        }
        const lowestValue = Math.min(...modification.options.map(option => option.value).filter(value => value > 0));
        return modification.options.find(option => option.value === lowestValue);
    }

    private getLowestTiresOption(modification: OfferModification): OfferModificationOption {
        const lowestValue = Math.min(...modification.options.map(option => option.value).filter(value => value > 0));
        return modification.options.find(option => option.value === lowestValue);
    }

    private createRadioConfigs(modification: OfferModification, initialConfig: boolean): any[] {
        const { code, options } = modification;
        let radioOptions;
        if (this.offerModificationsService.isModificationWithSelect(modification.code)) {
            const zeroValueOption = modification.options.find(option => option.value === 0 || option.value === 'NONE');
            const selectedOption = modification.code === OfferModificationCode.NNW ? modification.options.find(option => option.value === this.offerModificationsService.nnwValue) : modification.options.find(option => option.value === this.offerModificationsService.glassValue);
            radioOptions = [
                { value: zeroValueOption.value, label: 'OFFER_MODIFICATIONS.none', price: this.getOptionPrice(zeroValueOption) },
                {
                    value: [0, 'NONE'].includes(selectedOption.value) ? this.getDefaultPositiveOptionForModificationWithSelect(modification).value : selectedOption.value,
                    label: 'OFFER_MODIFICATIONS.yes',
                    price: [0, 'NONE'].includes(selectedOption.value) ? this.getOptionPrice(this.getDefaultPositiveOptionForModificationWithSelect(modification)) : this.getOptionPrice(selectedOption)
                }
            ];
        } else if (code === OfferModificationCode.AC_VARIANT) {
            const correctValue = this.offerModificationsService.depreciationPartValue ? AcVariantsModificationOption.KOSZTORYS_Z_AMORTYZACJA : AcVariantsModificationOption.KOSZTORYS_BEZ_AMORTYZACJI;
            const skippedValue = this.offerModificationsService.depreciationPartValue ? AcVariantsModificationOption.KOSZTORYS_BEZ_AMORTYZACJI : AcVariantsModificationOption.KOSZTORYS_Z_AMORTYZACJA;
            radioOptions = options.filter(o => o.value !== skippedValue).map(option => ({
                value: option.value,
                label: option.value !== correctValue ? this.languageService.getDescription(<any[]>option.labels) : 'OFFER_MODIFICATIONS.estimate',
                price: this.getOptionPrice(option)
            }));
        } else {
            radioOptions = options.map(option => ({
                value: option.value,
                label: this.languageService.getDescription(<any[]>option.labels),
                price: this.getOptionPrice(option),
                disabled: (modification.code === OfferModificationCode.AC_EXCESS_THIEF && option.value === 0 && this.offerModificationsService.acExcessNotLessThan1000) ||
                    (modification.code === OfferModificationCode.AC_EXCESS_THIEF && this.offerModificationsService.minicascoVariantSelectedOption === false) ||
                    (modification.code === OfferModificationCode.AC_GSU && !option.value && this.offerModificationsService.gsuIncluded && this.offerModificationsService.acVariantSerwisASO) ||
                    (modification.code === OfferModificationCode.REPAIR_PARTS && option.value === 'ORIGINAL' && !this.offerModificationsService.acVariantSerwisASOAvailable)
            }));
        }

        if (this.priceFrequency) {
            radioOptions.sort((a, b) => a.price - b.price);
        }

        const modificationConfig = this.offerModificationsService.configService.getModificationConfig(<OfferModificationCode>code);
        if (isNotNullOrUndefined(modificationConfig) && modificationConfig.hasOwnProperty('isOptionRecommended')) {
            radioOptions.forEach(radioOption => radioOption.highlighted = modificationConfig.isOptionRecommended(radioOption.value));
        }

        return radioOptions;
    }

    private getOptionPrice(option: OfferModificationOption): number {
        if (this.priceFrequency) {
            if (option.hasOwnProperty('prices')) {
                const priceForFrequency = option.prices.find(price => price.frequency === this.priceFrequency);
                return priceForFrequency ? priceForFrequency.value : undefined;
            }
            return 0;
        }
        return undefined;
    }

    private createSelectOptions(modification: OfferModification): any[] {
        const options = modification.options
            .filter(o => {
                if (_.isString(o.value)) { // Uniqa GLASS
                    return o.value.toUpperCase() !== 'NONE';
                }
                return o.value > 0;
            })
            .map(option => ({
                key: option.value,
                label: this.languageService.getDescription(<any[]>option.labels)
            }));
        if (modification.code === OfferModificationCode.NNW) {
            options.sort((a, b) => a.key - b.key);
        } else if (modification.code === OfferModificationCode.GLASS) {
            options.sort((a, b) => {
                const aOption = modification.options.find(option => option.value === a.key);
                const bOption = modification.options.find(option => option.value === b.key);
                return this.getOptionPrice(aOption) - this.getOptionPrice(bOption);
            });
        }

        return options;
    }
}
